import type { ReactNode } from "react";
import { assets } from "../../assets";
import { BrandImage } from "../BrandImage/BrandImage";
import { Wordmark } from "../BrandImage/Wordmark";
import { useTheme } from "../../theme";
import { ThemeToggle } from "../ThemeToggle/ThemeToggle";
import { AuthBrand } from "./AuthBrand";

export interface AuthStep {
  current: number;
  total: number;
  label: string;
}

export interface AuthLayoutProps {
  children: ReactNode;
  /** Wider card (privacy notice, recovery codes). */
  wide?: boolean;
  step?: AuthStep;
  /** Replaces the theme toggle at the top right. */
  topRight?: ReactNode;
}

/** Split auth layout: navy brand panel + form column. Shared by every auth step. */
export function AuthLayout({ children, wide, step, topRight }: AuthLayoutProps): React.JSX.Element {
  const { theme } = useTheme();
  return (
    <div className="nb-auth">
      <AuthBrand />
      <main className="nb-auth-main">
        <div className="nb-auth-top">
          <BrandImage src={theme === "dark" ? assets.logoDark : assets.logo} alt="Nabble Suite" className="nb-auth-top-logo" fallback={<Wordmark />} />
          <span />
          {topRight ?? <ThemeToggle />}
        </div>
        <div className="nb-auth-body">
          <div className={`nb-auth-card${wide ? " is-wide" : ""}`}>
            {step ? <AuthStepBar step={step} /> : null}
            {children}
          </div>
        </div>
        <p className="nb-auth-legal">Access for authorized people only</p>
      </main>
    </div>
  );
}

function AuthStepBar({ step }: { step: AuthStep }): React.JSX.Element {
  return (
    <div className="nb-auth-step">
      <span className="nb-auth-step-bar" aria-hidden="true">
        {Array.from({ length: step.total }).map((_, i) => (
          <i key={i} className={i < step.current ? "is-done" : undefined} />
        ))}
      </span>
      <span>{`Step ${step.current} of ${step.total}: ${step.label}`}</span>
    </div>
  );
}
